import {
    setInstrumentType,
    setUnderlyingSymbol,
    setExpirationDate,
    setOptionCallPut,
    setStrikePrice,
    setDropdownSearchTerm,
    setFocus,
    lostFocus,
    navigateDropdown,
    getElementDropdown,
} from '../state';
import type { InstrumentType, FocusedElement, SymbolPickerAction, SymbolPickerState } from '../state';
import { ARIA_LABELS, ELEMENT_SELECTORS } from '../types';
import type { OptionCallPut } from '../types';
import { useEffect, useRef } from 'react';
import { useEvent } from 'src/hooks/useEvent';
import { UnreachableCaseError } from 'src/lib/util/unreachableCaseError';

export const useSymbolPickerManager = (state: SymbolPickerState, dispatch: (action: SymbolPickerAction) => void) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const isProgrammaticFocusRef = useRef(false);
    const isTabNavigatingRef = useRef(false);

    // Move DOM focus to the element that matches state.focusedElement.
    // Depend on focusedElement alone: depending on the whole state object caused
    // trailing async updates (e.g. dropdown items loading) to refocus the field
    // mid-blur and trap focus in the strike price input (PLAT-12159).
    // Cancel the scheduled rAF on cleanup so that a stale focus request doesn't
    // fire after the state has moved on (e.g. lostFocus after setFocus) — that
    // left the container with tabIndex removed and broke subsequent tab-in.
    useEffect(() => {
        if (state.focusedElement && containerRef.current) {
            const selector = ELEMENT_SELECTORS[state.focusedElement];
            const element = containerRef.current.querySelector(selector) as HTMLElement;

            if (element) {
                isProgrammaticFocusRef.current = true;

                const rafId = requestAnimationFrame(() => {
                    element.focus();
                    isProgrammaticFocusRef.current = false;
                    // remove tabIndex=0 from our container when focus is gained,
                    // so tabbing backwards leaves to the next DOM element and not our container
                    containerRef.current?.removeAttribute('tabIndex');
                });
                return () => {
                    cancelAnimationFrame(rafId);
                    isProgrammaticFocusRef.current = false;
                };
            }
        }
    }, [state.focusedElement]);

    // Centralized focus handler for individual inputs
    // this should be applied to each input's onFocus handler
    const handleInputFocus = useEvent((element: FocusedElement) => {
        // Only dispatch SET_FOCUS if this is NOT a programmatic focus change
        if (!isProgrammaticFocusRef.current) {
            dispatch(setFocus(element));
        }
    });

    // Handle initial focus when tabbing into the component
    const handleContainerFocus = useEvent((event: React.FocusEvent) => {
        if (!containerRef.current) {
            return;
        }
        // Only handle focus if it's coming to the container itself, not child elements
        if (event.target === containerRef.current) {
            // Ignore container focus during tab navigation to prevent interference
            if (isTabNavigatingRef.current) {
                return;
            }

            // Find the first element in the container that matches any ELEMENT_SELECTORS
            let firstKey: FocusedElement | null = null;
            for (const [key, selector] of Object.entries(ELEMENT_SELECTORS)) {
                const el = containerRef.current.querySelector(selector);
                if (el) {
                    firstKey = key as FocusedElement;
                    break;
                }
            }

            if (firstKey) {
                dispatch(setFocus(firstKey));
                return;
            }
        }
    });

    // Helper function to handle Enter key press
    const handleEnterKey = useEvent(() => {
        if (!state.focusedElement) return;

        const currentDropdown = getElementDropdown(state, state.focusedElement);

        // Determine the value to set
        let valueToSet: any;
        if (currentDropdown.filteredItems.length > 0) {
            // Select the currently highlighted item
            const selectedItem = currentDropdown.filteredItems[currentDropdown.activeIndex];
            if (!selectedItem) return;

            // Transform the selected item based on element type
            switch (state.focusedElement) {
                case 'underlyingSymbol':
                    valueToSet = selectedItem.symbol || selectedItem;
                    break;
                case 'strikePrice':
                    valueToSet = Number(selectedItem);
                    break;
                default:
                    valueToSet = selectedItem;
                    break;
            }
        } else {
            // Use the search term as the value (including empty string)
            const searchTerm = currentDropdown.searchTerm || '';
            switch (state.focusedElement) {
                case 'strikePrice':
                    valueToSet = Number(searchTerm) || 0;
                    break;
                default:
                    valueToSet = searchTerm;
                    break;
            }
        }

        // Dispatch the appropriate action
        switch (state.focusedElement) {
            case 'instrumentType':
                dispatch(setInstrumentType(valueToSet as InstrumentType, 'forward'));
                break;
            case 'underlyingSymbol':
                dispatch(setUnderlyingSymbol(valueToSet, 'forward'));
                break;
            case 'expirationDate':
                dispatch(setExpirationDate(valueToSet, 'forward'));
                break;
            case 'optionCallPut':
                dispatch(setOptionCallPut(valueToSet as OptionCallPut, 'forward'));
                break;
            case 'strikePrice':
                dispatch(setStrikePrice(valueToSet, 'forward'));
                break;
            default:
                throw new UnreachableCaseError(state.focusedElement);
        }
    });

    // Helper function to handle Escape key press
    const handleEscapeKey = useEvent(() => {
        if (!state.focusedElement) return;
        dispatch(setDropdownSearchTerm(state.focusedElement, ''));
    });

    // Helper function to handle Tab key press
    const handleTabKey = useEvent((direction: 'forward' | 'backward') => {
        if (!state.focusedElement) return;

        const currentDropdown = getElementDropdown(state, state.focusedElement);

        // Determine the value to set based on search term
        let valueToSet: any;
        if (currentDropdown.searchTerm) {
            valueToSet = currentDropdown.searchTerm;
        } else {
            // If search term is undefined or empty, reset to the original field value
            switch (state.focusedElement) {
                case 'instrumentType':
                    valueToSet = state.instrument.instrumentType;
                    break;
                case 'underlyingSymbol':
                    valueToSet = state.instrument.underlyingSymbol || '';
                    break;
                case 'expirationDate':
                    valueToSet = state.instrument.expirationDate || '';
                    break;
                case 'optionCallPut':
                    valueToSet = state.instrument.optionCallPut || undefined;
                    break;
                case 'strikePrice':
                    valueToSet = state.instrument.strikePrice || undefined;
                    break;
                default:
                    throw new UnreachableCaseError(state.focusedElement);
            }
        }

        // Set the determined value with move-focus intent
        // Tab always moves focus in the specified direction

        // Dispatch the appropriate action
        switch (state.focusedElement) {
            case 'instrumentType':
                dispatch(setInstrumentType(valueToSet as InstrumentType, direction));
                break;
            case 'underlyingSymbol':
                dispatch(setUnderlyingSymbol(valueToSet, direction));
                break;
            case 'expirationDate':
                dispatch(setExpirationDate(valueToSet, direction));
                break;
            case 'optionCallPut':
                dispatch(setOptionCallPut(valueToSet, direction));
                break;
            case 'strikePrice':
                {
                    const parsedValue = Number(valueToSet);
                    dispatch(setStrikePrice(Number.isNaN(parsedValue) ? undefined : parsedValue, direction));
                }
                break;
            default:
                throw new UnreachableCaseError(state.focusedElement);
        }
    });

    // Centralized keyboard event handler for the entire component
    const handleContainerKeyDown = useEvent((event: React.KeyboardEvent) => {
        switch (event.key) {
            case 'ArrowDown':
            case 'ArrowUp': {
                // For arrow keys, we need to determine which element is focused based on aria-label
                const target = event.target as HTMLElement;
                const ariaLabel = target.getAttribute('aria-label');

                // Find the element type by matching aria-label
                let elementType: FocusedElement | null = null;
                for (const [key, label] of Object.entries(ARIA_LABELS)) {
                    if (label === ariaLabel) {
                        elementType = key as FocusedElement;
                        break;
                    }
                }

                if (!elementType) return;

                event.preventDefault();
                const direction = event.key === 'ArrowDown' ? 'down' : 'up';
                dispatch(navigateDropdown(elementType, direction));
                break;
            }
            case 'Enter':
                handleEnterKey();
                break;
            case 'Escape':
                event.preventDefault();
                handleEscapeKey();
                break;
            case 'Tab':
                // Handle both Tab (forward) and Shift+Tab (backward)
                const direction = event.shiftKey ? 'backward' : 'forward';

                // Set flag to indicate tab navigation is in progress
                isTabNavigatingRef.current = true;

                handleTabKey(direction);

                // Clear flag after a short delay to allow for focus settling
                setTimeout(() => {
                    isTabNavigatingRef.current = false;
                }, 100);
                break;
            default:
                break;
        }
    });

    // Parent-level blur handler to detect when focus leaves the entire component
    const handleContainerBlur = useEvent((event: React.FocusEvent) => {
        const relatedTarget = event.relatedTarget as HTMLElement;

        // Only dispatch LOST_FOCUS if focus is moving completely outside our container
        if (!relatedTarget || !containerRef.current?.contains(relatedTarget)) {
            // Use a small timeout to avoid interfering with programmatic focus changes
            requestAnimationFrame(() => {
                // Double-check that focus hasn't moved to another element in our container
                if (!containerRef.current?.contains(document.activeElement)) {
                    dispatch(lostFocus());
                    // add tabIndex=0 to our container when focus is completely lost
                    // so we can tab back into the symbol picker group
                    containerRef.current?.setAttribute('tabIndex', '0');
                }
            });
        }
    });

    return {
        containerRef,
        handleInputFocus,
        handleContainerFocus,
        handleContainerKeyDown,
        handleContainerBlur,
    };
};
